import Vue from "vue";
import { newLogger } from "@/utils/util";
import network from "../../../utils/network";
import {
  CONDITIONAL_FIELD_OPERATOR,
  FLOW_QUESTION_FIELD_SUBTYPE,
  FLOW_QUESTION_FIELD_TYPE,
  SIMPLE_FIELD_TYPE,
} from "../../../utils/const";

let logger = newLogger("LoanApplicationStore");

const urls = {
  createLoanApplication: "/credit/loan-application", // POST
  myLoanApplications: "/credit/my-loan-applications", // GET
  myLoanApplication: `/credit/my-loan-application`, // GET
  myLoanApplicationWithId: (id) => `/credit/my-loan-application/${id}`, // GET
  getSigningDocuments: `/credit/my-loan-application/documents-for-signing`,
  getScoringDecision: (applicationId) =>
    `/credit/my-loan-application/scoring-decision/${applicationId}`,
  signatureErrorFound: `/credit/loan-application-signature/error-found`,

  getPreviousDocuments: (promptId) =>
    `/credit/my-loan-application/document/${promptId}`,
  pollStatus: `/credit/my-loan-application/status`, // GET
  getFlow: `/credit/my-loan-application/flow`, // GET

  updateData: `/credit/my-loan-application/data`, // PATCH
  updatePosition: `/credit/my-loan-application/update-position`, // PATCH
  close: `/credit/my-loan-application/close`, // POST
  finish: `/credit/my-loan-application/finish`, // POST
  uploadFile: `/credit/my-loan-application/document`, // PATCH

  startKyc: `/credit/my-loan-application/identification/start`, // POST
  startSign: `/credit/my-loan-application/document-signing/start`, // POST
  startScoringEvaluation: `/credit/loan-application/evaluate-scoring`,

  baseData: (applicationId) =>
    `/credit/my-loan-application-base-data/${applicationId}`,
};

/**
 * Credit Loan Application store
 */

const initState = () => {
  return {
    loanApplication: {},
    loanApplicationWithId: {},
    loanApplications: [],
    selectedOffer: {},
    flow: [],
    applicationData: {},
    signatureProcess: {},
    comnicaRecord: {},
    loaded: false,
    kycMobile: false,
    signMobile: false,
    baseData: {},
  };
};

const actions = {
  createApplication: async ({ commit }, request) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.post(urls.createLoanApplication, request);

      commit("saveLoanApplication", data);

      return Promise.resolve({ offerId: data.offerId, flowId: data.flowId });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  loadEverything: async ({ dispatch, commit }) => {
    let data = await dispatch("fetchApplication");
    await dispatch("fetchFlow", data.flowId);
    await dispatch("fetchOffer", {
      offerId: data.offerId,
      loanApplicationId: data.id,
    });
    commit("loaded");
    return true;
  },
  fetchSignatureProcess: async ({ commit }) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.get(urls.getSigningDocuments);
      commit("saveSignatureProcess", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchPreviousDocuments: async ({}, promptId) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.get(urls.getPreviousDocuments(promptId));
      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchMyApplications: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.myLoanApplications);

      commit("saveLoanApplications", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchApplication: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.myLoanApplication);

      commit("saveLoanApplication", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchAndGetScoringDecision: async ({}, applicationId) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.getScoringDecision(applicationId));
      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchApplicationWithId: async ({ commit }, id) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.myLoanApplicationWithId(id));

      commit("saveLoanApplicationWithId", { data: data, id: id });

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchFlow: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.getFlow);

      commit("saveFlow", data);

      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchOffer: async (
    { commit },
    { offerId: id, loanApplicationId: loanApplicationId }
  ) => {
    const offerUrls = {
      offerDatas: "/administration/offer-data",
      applicationBaseData: (loanApplicationId) =>
        `/credit/my-loan-application-base-data/${loanApplicationId}`,
    };
    try {
      let axios = await network.connection();
      let baseDataResponse = await axios.get(
        offerUrls.applicationBaseData(loanApplicationId)
      );

      let query = `?ids=${id}`;
      let { data } = await axios.get(offerUrls.offerDatas + query);
      let offer = {
        calculation: baseDataResponse.data,
        data: data[0],
      };
      commit("saveOffer", offer);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  pollStatus: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.pollStatus);

      commit("updateStatus", data);

      return Promise.resolve(data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  saveDynamicData: async ({ commit }, { key, value }) => {
    try {
      commit("updateDynamicData", { key, value });
      const axios = await network.connection();
      await axios.patch(urls.updateData, {
        key,
        value,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  close: async ({}) => {
    // todo local update
    try {
      const axios = await network.connection();
      await axios.post(urls.close);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  finish: async () => {
    try {
      const axios = await network.connection();
      await axios.post(urls.finish);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  signatureErrorFound: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.patch(urls.signatureErrorFound, request);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  savePosition: async ({ commit }, { flowPosition, currentFlowStepIndex }) => {
    try {
      const axios = await network.connection();
      await axios.patch(urls.updatePosition, {
        flowPosition: flowPosition,
        currentFlowIndex: currentFlowStepIndex,
      });

      commit("updatePosition", {
        flowPosition,
        currentFlowStepIndex,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  setKycMobile: ({ commit }, val) => {
    commit("updateKycMobile", val);
  },
  setSignMobile: ({ commit }, val) => {
    commit("updateSignMobile", val);
  },
  startKyc: async ({ commit }, kycType) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.post(urls.startKyc, { type: kycType });

      commit("saveComnica", data);
      commit("updatePosition", {
        flowPosition: "KYC_WAITING",
        currentFlowStepIndex: false,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  startSign: async ({ commit }, sigType) => {
    try {
      const axios = await network.connection();
      let { data } = await axios.post(urls.startSign, { type: sigType });

      commit("saveComnica", data);
      commit("updatePosition", {
        flowPosition: "SIGN_WAITING",
        currentFlowStepIndex: false,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  startScoringEvaluation: async ({}) => {
    try {
      const axios = await network.connection();
      await axios.patch(urls.startScoringEvaluation);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  uploadFile: async ({}, { file, promptId, promptIdentifier }) => {
    try {
      const axios = await network.connection();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("promptId", promptId);
      formData.append("promptIdentifier", promptIdentifier);
      await axios.patch(urls.uploadFile, formData);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  fetchBaseData: async ({ commit }, loanApplicationId) => {
    try {
      let axios = await network.connection();
      let { data } = await axios.get(urls.baseData(loanApplicationId));
      commit("saveBaseData", { data, loanApplicationId });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
};

const mutations = {
  saveLoanApplication: (state, data) => {
    Vue.set(state, "applicationData", JSON.parse(data.dynamicData));
    Vue.set(state, "loanApplication", data);
    if (!state.loanApplication.kycTypes) {
      state.loanApplication.kycTypes = [];
    }
    if (!state.loanApplication.sigTypes) {
      state.loanApplication.sigTypes = [];
    }
    if (data.projectRecords && data.projectRecords.length)
      if (data.projectRecords.length === 1) {
        Vue.set(state, "comnicaRecord", data.projectRecords[0]);
      } else {
        let records = data.projectRecords.sort(
          (a, b) => a.recordId - b.recordId
        );
        Vue.set(state, "comnicaRecord", records[records.length - 1]);
      }
  },
  saveLoanApplications: (state, data) => {
    Vue.set(state, "loanApplications", data);
  },
  saveLoanApplicationWithId: (state, { data, id }) => {
    Vue.set(state.loanApplicationWithId, id, data);
  },
  saveSignatureProcess: (state, data) => {
    Vue.set(state, "signatureProcess", data);
  },
  saveOffer: (state, offer) => {
    Vue.set(state, "selectedOffer", offer);
  },
  saveFlow: (state, flow) => {
    if (state.flow.length) {
      Vue.set(state, "flow", []);
    }
    let steps = flow.steps.sort((a, b) => {
      return a.rowIndex - b.rowIndex;
    });
    for (let s in steps) {
      steps[s].questions = steps[s].questions.filter((q) => !q.hidden);
      state.flow.push(steps[s]);
    }
  },
  updateDynamicData: (state, { key, value }) => {
    Vue.set(state.applicationData, key, value);
  },
  updatePosition: (state, { flowPosition, currentFlowStepIndex }) => {
    Vue.set(state.loanApplication, "currentPosition", flowPosition);
    if (currentFlowStepIndex || currentFlowStepIndex === 0)
      Vue.set(state.loanApplication, "currentFlowIndex", currentFlowStepIndex);
  },
  loaded: (state) => {
    Vue.set(state, "loaded", true);
  },
  updateKycMobile: (state, val) => {
    Vue.set(state, "kycMobile", val);
  },
  updateSignMobile: (state, val) => {
    Vue.set(state, "signMobile", val);
  },
  saveComnica: (state, data) => {
    Vue.set(state, "comnicaRecord", data);
  },
  updateStatus: (state, data) => {
    Vue.set(state.loanApplication, "currentStatus", data.currentStatus);
    Vue.set(
      state.loanApplication,
      "currentStatusResult",
      data.currentStatusResult
    );
    Vue.set(state.loanApplication, "currentPosition", data.currentPosition);
  },
  saveBaseData: (state, { data, loanApplicationId }) => {
    Vue.set(state.baseData, loanApplicationId, data);
  },
};

const getters = {
  getActiveOffer: (state) => {
    return state.selectedOffer;
  },
  getActiveApplication: (state) => {
    return state.loanApplication;
  },
  getApplication: (state) => (id) => {
    return state.loanApplicationWithId[id];
  },
  loaded: (state) => {
    return state.loaded;
  },
  flow: (state) => {
    return state.flow;
  },
  getFlowQuestionAnswer: (state) => (identifier) => {
    return state.applicationData[identifier];
  },
  isKycMobile: (state) => {
    return state.kycMobile;
  },
  getSignatureProcess: (state) => {
    return state.signatureProcess;
  },
  isSignMobile: (state) => {
    return state.signMobile;
  },
  applicationStatus: (state) => {
    return state.loanApplication.currentStatus;
  },
  applicationStatusResult: (state) => {
    return state.loanApplication.currentStatusResult;
  },
  canContinue: (state, getters, rootState) => (step) => {
    for (let q in state.flow[step].questions) {
      let question = state.flow[step].questions[q];
      let field;
      switch (question.fieldType) {
        case FLOW_QUESTION_FIELD_TYPE.SIMPLE_FIELD:
          field = rootState.simpleFieldStore.fieldData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT:
          field =
            rootState.statementPromptStore.statementData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.DOCUMENT_PROMPT:
          field = rootState.documentPromptStore.documentData[question.fieldId];
          break;
        case FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD:
          if (question.fieldSubtype === FLOW_QUESTION_FIELD_SUBTYPE.PSD2) {
            field = rootState.psd2FieldStore.psd2FieldData[question.fieldId];
          }
          break;
        case FLOW_QUESTION_FIELD_TYPE.READONLY_FIELD:
          continue;
      }
      if (question.isRequired) {
        let needAnswer;
        if (question.isConditional) {
          for (const condition of question.conditions) {
            let index = question.conditions.indexOf(condition);

            let dependency =
              rootState.simpleFieldStore.fieldData[
                condition.conditionalFieldId
              ];

            let evaluate;

            let answer = state.applicationData[dependency.identifier];
            if (dependency.type === SIMPLE_FIELD_TYPE.CHECKBOX) {
              if (answer) {
                let answerObject = JSON.parse(answer);
                evaluate = answerObject.includes(
                  condition.conditionalFieldAnswer
                );
              } else {
                evaluate = false;
              }
            } else {
              evaluate = answer === condition.conditionalFieldAnswer;
            }

            if (index === 0) {
              needAnswer = evaluate;
            } else {
              let operator =
                question.conditions[index - 1].conditionalFieldOperator;
              if (CONDITIONAL_FIELD_OPERATOR.ENUM.AND === operator) {
                needAnswer = needAnswer && evaluate;
              } else if (CONDITIONAL_FIELD_OPERATOR.ENUM.OR === operator) {
                needAnswer = needAnswer || evaluate;
              }
            }
          }

          if (needAnswer) {
            if (
              question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
              question.fieldSubtype === FLOW_QUESTION_FIELD_SUBTYPE.PSD2
            ) {
              if (!field.completed) {
                console.warn("Conditionally required PSD2 not synced!");
                return false;
              }
            } else {
              if (!state.applicationData[field.identifier]) {
                console.warn("Conditionally required failed!");
                return false;
              }
            }

            if (question.isHardKo) {
              if (
                state.applicationData[field.identifier] === question.koAnswer
              ) {
                console.warn("Conditionally KO failed!");
                return false;
              }
            }
            if (
              field &&
              field.type === SIMPLE_FIELD_TYPE.INPUT &&
              field.validation
            ) {
              let value = state.applicationData[field.identifier];

              if (value === undefined) {
                value = "";
              }

              const regex = new RegExp(field.validation);
              if (!regex.test(value)) {
                console.warn("Conditionally regex validation failed!");
                return false;
              }
            }
          }
        } else {
          if (
            question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
            question.fieldSubtype === FLOW_QUESTION_FIELD_SUBTYPE.PSD2
          ) {
            if (!field.completed) {
              console.warn("Required PSD2 not synced!");
              return false;
            }
          } else {
            if (!state.applicationData[field.identifier]) {
              console.warn("Required failed!");
              return false;
            }
          }

          if (question.isHardKo) {
            if (state.applicationData[field.identifier] === question.koAnswer) {
              console.warn("KO failed!");
              return false;
            }
          }
          if (
            field &&
            field.type === SIMPLE_FIELD_TYPE.INPUT &&
            field.validation
          ) {
            let value = state.applicationData[field.identifier];

            if (value === undefined) {
              value = "";
            }

            const regex = new RegExp(field.validation);
            if (!regex.test(value)) {
              console.warn("Regex validation failed!");
              return false;
            }
          }
        }
      }
    }
    return true;
  },
  getLoanApplications: (state) => {
    return state.loanApplications;
  },
  comnicaUrl: (state) => {
    return state.comnicaRecord.url;
  },
  baseData: (state) => (loaApplicationId) => {
    return state.baseData[loaApplicationId];
  },
};

export default {
  namespaced: true,
  state: initState(),
  mutations: mutations,
  actions: actions,
  getters: getters,
};
